import React, { useMemo, useState } from "react";
import {
  Box, Paper, Chip, Stack, Button, TextField, Tabs, Tab, Dialog, DialogTitle,
  DialogContent, DialogActions, Typography, Checkbox, FormControlLabel,
  Autocomplete, IconButton, Tooltip, Divider,
} from "@mui/material";
import { Plus, Trash2, Pencil, Lock } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { identityService } from "../../api/services/identityService";
import DataTable from "../../components/DataTable";
import { PageHeader, ConfirmDialog } from "../../components/Common";

export default function RolesAndTeamsPage() {
  const [tab, setTab] = useState(0);
  return (
    <Box>
      <PageHeader title="Roles & Teams" subtitle="Manage ATS roles, permissions, and support teams" />
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: "1px solid #eee" }}>
        <Tab label="Roles" />
        <Tab label="Teams" />
      </Tabs>
      {tab === 0 ? <RolesTab /> : <TeamsTab />}
    </Box>
  );
}

// ============================== Roles ==============================

const emptyRoleForm = { code: "", name: "", description: "", permission_codes: [] };

function RolesTab() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null); // null = create mode
  const [form, setForm] = useState(emptyRoleForm);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const { data: roles, isLoading } = useQuery({ queryKey: ["roles"], queryFn: identityService.listRoles });
  const { data: permissions, isLoading: permsLoading } = useQuery({
    queryKey: ["permissions"],
    queryFn: identityService.listPermissions,
  });

  const openCreate = () => {
    setEditingRole(null);
    setForm(emptyRoleForm);
    setDialogOpen(true);
  };

  const openEdit = (role) => {
    setEditingRole(role);
    setForm({ code: role.code, name: role.name, description: role.description || "", permission_codes: role.permission_codes });
    setDialogOpen(true);
  };

  const saveMutation = useMutation({
    mutationFn: () =>
      editingRole
        ? identityService.updateRole(editingRole.id, {
            name: form.name,
            description: form.description,
            permission_codes: form.permission_codes,
          })
        : identityService.createRole(form),
    onSuccess: () => {
      enqueueSnackbar(editingRole ? "Role updated" : "Role created", { variant: "success" });
      queryClient.invalidateQueries({ queryKey: ["roles"] });
      setDialogOpen(false);
    },
    onError: (err) => enqueueSnackbar(err.response?.data?.detail || "Failed to save role", { variant: "error" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => identityService.deleteRole(id),
    onSuccess: () => {
      enqueueSnackbar("Role deleted", { variant: "success" });
      queryClient.invalidateQueries({ queryKey: ["roles"] });
      setDeleteTarget(null);
    },
    onError: (err) => {
      enqueueSnackbar(err.response?.data?.detail || "Could not delete role", { variant: "error" });
      setDeleteTarget(null);
    },
  });

  const columns = [
    { key: "name", label: "Role", render: (r) => <strong>{r.name}</strong> },
    { key: "code", label: "Code" },
    { key: "is_system_role", label: "Type", render: (r) => (r.is_system_role ? "System" : "Custom") },
    {
      key: "permission_codes", label: "Permissions",
      render: (r) => (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {r.permission_codes.slice(0, 4).map((p) => <Chip key={p} size="small" label={p} />)}
          {r.permission_codes.length > 4 && <Chip size="small" label={`+${r.permission_codes.length - 4} more`} />}
        </Stack>
      ),
    },
    {
      key: "actions", label: "", width: 100,
      render: (r) => (
        <Stack direction="row" spacing={0.5} onClick={(e) => e.stopPropagation()}>
          <IconButton size="small" onClick={() => openEdit(r)}>
            <Pencil size={16} />
          </IconButton>
          {r.is_system_role ? (
            <Tooltip title="System roles can't be deleted, only edited">
              <span>
                <IconButton size="small" disabled><Lock size={16} /></IconButton>
              </span>
            </Tooltip>
          ) : (
            <IconButton size="small" color="error" onClick={() => setDeleteTarget(r)}>
              <Trash2 size={16} />
            </IconButton>
          )}
        </Stack>
      ),
    },
  ];

  const codeTaken = !editingRole && (roles || []).some((r) => r.code.toLowerCase() === form.code.trim().toLowerCase());
  const canSave = editingRole
    ? form.name.trim().length > 0
    : form.code.trim().length > 0 && form.name.trim().length > 0 && !codeTaken;

  return (
    <Box>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        <Button variant="contained" startIcon={<Plus size={16} />} onClick={openCreate}>New Role</Button>
      </Stack>

      <DataTable
        columns={columns}
        rows={roles}
        loading={isLoading}
        emptyMessage="No roles found"
        onRowClick={openEdit}
      />

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{editingRole ? `Edit role — ${editingRole.name}` : "New Role"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Code" value={form.code} disabled={Boolean(editingRole)}
                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().replace(/\s+/g, "_") })}
                fullWidth required
                error={codeTaken}
                helperText={editingRole ? "Code can't be changed after creation" : codeTaken ? "Code already in use" : "e.g. TEAM_LEAD"}
              />
              <TextField
                label="Name" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                fullWidth required
              />
            </Stack>
            <TextField
              label="Description" value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              fullWidth multiline minRows={2}
            />
            <Divider />
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Permissions</Typography>
            <PermissionPicker
              permissions={permissions}
              loading={permsLoading}
              selected={form.permission_codes}
              onChange={(codes) => setForm({ ...form, permission_codes: codes })}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDialogOpen(false)} color="inherit">Cancel</Button>
          <Button variant="contained" disabled={!canSave || saveMutation.isPending} onClick={() => saveMutation.mutate()}>
            {saveMutation.isPending ? "Saving..." : editingRole ? "Save changes" : "Create role"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete role?"
        message={`This deletes "${deleteTarget?.name}". Anyone currently assigned this role will lose the permissions it grants.`}
        confirmLabel="Delete" danger
        loading={deleteMutation.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => deleteMutation.mutate(deleteTarget.id)}
      />
    </Box>
  );
}

function PermissionPicker({ permissions, loading, selected, onChange }) {
  const grouped = useMemo(() => {
    const groups = {};
    for (const p of permissions || []) {
      groups[p.module] = groups[p.module] || [];
      groups[p.module].push(p);
    }
    return groups;
  }, [permissions]);

  if (loading) return <Typography variant="body2" color="text.secondary">Loading permissions...</Typography>;

  const toggle = (code) => {
    onChange(selected.includes(code) ? selected.filter((c) => c !== code) : [...selected, code]);
  };

  const toggleModule = (modulePerms, allSelected) => {
    const codes = modulePerms.map((p) => p.code);
    onChange(allSelected ? selected.filter((c) => !codes.includes(c)) : Array.from(new Set([...selected, ...codes])));
  };

  return (
    <Stack spacing={1.5} sx={{ maxHeight: 360, overflowY: "auto", pr: 1 }}>
      {Object.entries(grouped).map(([module, modulePerms]) => {
        const allSelected = modulePerms.every((p) => selected.includes(p.code));
        const someSelected = modulePerms.some((p) => selected.includes(p.code));
        return (
          <Paper key={module} variant="outlined" sx={{ p: 1.5 }}>
            <FormControlLabel
              sx={{ mb: 0.5 }}
              control={
                <Checkbox
                  size="small"
                  checked={allSelected}
                  indeterminate={someSelected && !allSelected}
                  onChange={() => toggleModule(modulePerms, allSelected)}
                />
              }
              label={<Typography variant="subtitle2" sx={{ fontWeight: 700, textTransform: "capitalize" }}>{module}</Typography>}
            />
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", pl: 2 }}>
              {modulePerms.map((p) => (
                <FormControlLabel
                  key={p.code}
                  control={<Checkbox size="small" checked={selected.includes(p.code)} onChange={() => toggle(p.code)} />}
                  label={<Typography variant="body2">{p.description || p.code}</Typography>}
                />
              ))}
            </Box>
          </Paper>
        );
      })}
    </Stack>
  );
}

// ============================== Teams ==============================

function TeamsTab() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null); // null = create mode
  const [deleteTarget, setDeleteTarget] = useState(null);

  const { data: teams, isLoading } = useQuery({ queryKey: ["teams"], queryFn: identityService.listTeams });
  const { data: employees } = useQuery({ queryKey: ["employees"], queryFn: identityService.listEmployees });

  const employeeById = useMemo(() => {
    const map = new Map();
    (employees || []).forEach((e) => map.set(e.id, e));
    return map;
  }, [employees]);

  const deleteMutation = useMutation({
    mutationFn: (id) => identityService.deleteTeam(id),
    onSuccess: () => {
      enqueueSnackbar("Team deleted", { variant: "success" });
      queryClient.invalidateQueries({ queryKey: ["teams"] });
      setDeleteTarget(null);
    },
    onError: (err) => {
      enqueueSnackbar(err.response?.data?.detail || "Could not delete team", { variant: "error" });
      setDeleteTarget(null);
    },
  });

  const columns = [
    { key: "name", label: "Team", render: (t) => <strong>{t.name}</strong> },
    {
      key: "team_lead_employee_id", label: "Manager",
      render: (t) => (t.team_lead_employee_id ? employeeById.get(t.team_lead_employee_id)?.name || `#${t.team_lead_employee_id}` : "—"),
    },
    {
      key: "member_employee_ids", label: "Members",
      render: (t) => (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {t.member_employee_ids.slice(0, 3).map((id) => (
            <Chip key={id} size="small" label={employeeById.get(id)?.name || `#${id}`} />
          ))}
          {t.member_employee_ids.length > 3 && <Chip size="small" label={`+${t.member_employee_ids.length - 3} more`} />}
          {t.member_employee_ids.length === 0 && <Typography variant="body2" color="text.secondary">No members</Typography>}
        </Stack>
      ),
    },
    {
      key: "actions", label: "", width: 100,
      render: (t) => (
        <Stack direction="row" spacing={0.5} onClick={(e) => e.stopPropagation()}>
          <IconButton size="small" onClick={() => { setEditingTeam(t); setDialogOpen(true); }}>
            <Pencil size={16} />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => setDeleteTarget(t)}>
            <Trash2 size={16} />
          </IconButton>
        </Stack>
      ),
    },
  ];

  return (
    <Box>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        <Button
          variant="contained" startIcon={<Plus size={16} />}
          onClick={() => { setEditingTeam(null); setDialogOpen(true); }}
        >
          New Team
        </Button>
      </Stack>

      <DataTable
        columns={columns}
        rows={teams}
        loading={isLoading}
        emptyMessage="No teams yet"
        onRowClick={(t) => { setEditingTeam(t); setDialogOpen(true); }}
      />

      {dialogOpen && (
        <TeamFormDialog
          team={editingTeam}
          employees={employees || []}
          onClose={() => setDialogOpen(false)}
        />
      )}

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete team?"
        message={`This deletes "${deleteTarget?.name}". Teams with assigned tickets can't be deleted until those tickets are reassigned.`}
        confirmLabel="Delete" danger
        loading={deleteMutation.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => deleteMutation.mutate(deleteTarget.id)}
      />
    </Box>
  );
}

function TeamFormDialog({ team, employees, onClose }) {
  const isEdit = Boolean(team);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [name, setName] = useState(team?.name || "");
  const [manager, setManager] = useState(employees.find((e) => e.id === team?.team_lead_employee_id) || null);
  const [members, setMembers] = useState(
    (team?.member_employee_ids || []).map((id) => employees.find((e) => e.id === id)).filter(Boolean)
  );

  const saveMutation = useMutation({
    mutationFn: async () => {
      const memberIds = members.map((m) => m.id);
      if (!isEdit) {
        return identityService.createTeam({
          name,
          team_lead_employee_id: manager?.id ?? null,
          member_employee_ids: memberIds,
        });
      }

      // Editing: update name/manager, then reconcile membership deltas
      // against the team's original member list.
      const originalIds = new Set(team.member_employee_ids);
      const nextIds = new Set(memberIds);
      const toAdd = memberIds.filter((id) => !originalIds.has(id));
      const toRemove = team.member_employee_ids.filter((id) => !nextIds.has(id));

      await identityService.updateTeam(team.id, {
        name,
        team_lead_employee_id: manager?.id ?? null,
        clear_team_lead: !manager,
      });
      for (const id of toAdd) await identityService.addTeamMember(team.id, id);
      for (const id of toRemove) await identityService.removeTeamMember(team.id, id);
      return true;
    },
    onSuccess: () => {
      enqueueSnackbar(isEdit ? "Team updated" : "Team created", { variant: "success" });
      queryClient.invalidateQueries({ queryKey: ["teams"] });
      onClose();
    },
    onError: (err) => enqueueSnackbar(err.response?.data?.detail || "Failed to save team", { variant: "error" }),
  });

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{isEdit ? `Edit team — ${team.name}` : "New Team"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <TextField label="Team name" value={name} onChange={(e) => setName(e.target.value)} fullWidth required />

          <Autocomplete
            options={employees}
            getOptionLabel={(e) => e.name || e.email}
            value={manager}
            onChange={(_, v) => setManager(v)}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(params) => <TextField {...params} label="Team manager" placeholder="Select manager" />}
          />

          <Autocomplete
            multiple
            options={employees}
            getOptionLabel={(e) => e.name || e.email}
            value={members}
            onChange={(_, v) => setMembers(v)}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => <Chip size="small" label={option.name} {...getTagProps({ index })} />)
            }
            renderInput={(params) => <TextField {...params} label="Team members" placeholder="Add members" />}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button variant="contained" disabled={!name.trim() || saveMutation.isPending} onClick={() => saveMutation.mutate()}>
          {saveMutation.isPending ? "Saving..." : isEdit ? "Save changes" : "Create team"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
